import React, { useEffect, useMemo, useRef } from 'react';

import {
    buildPendoAccountMetadata,
    buildPendoInitSignature,
    buildPendoVisitorMetadata,
} from '@monolith/components/elements/navbar-1/pendoMetadata';
import loadPendoScript, { syncPendoIdentity } from '@monolith/pendoUtils';

import { OnboardingCurrentAccount } from '../../onboarding-navigation/infrastructure/useCurrentAccount';

export interface PendoProviderProps {
    account?: OnboardingCurrentAccount;
    apiKey?: string;
    business: {
        name?: string | null;
        fullName?: string | null;
        status?: string | null;
        smsFeatureEnabled?: boolean | null;
        federatedLoginEnabled?: boolean | null;
        marketingPlusEnabled?: boolean | null;
        campaignFeatureEnabled?: boolean | null;
        ongoingCampaignsEnabled?: boolean | null;
        crmEnabled?: boolean | null;
        advancedPayrollSettingsEnabled?: boolean | null;
        voipEnabled?: boolean | null;
        advancedReportingEnabled?: boolean | null;
        webhooksEnabled?: boolean | null;
        lessonMateFeatureEnabled?: boolean | null;
        videoRecordingRequestEnabled?: boolean | null;
        mailchimpFeatureEnabled?: boolean | null;
    };
    children: React.ReactNode;
}

export const PendoProvider = ({
    account,
    apiKey,
    business,
    children,
}: PendoProviderProps) => {
    const hasPendoInitializedRef = useRef(false);
    const lastPendoInitSignatureRef = useRef<string | null>(null);
    const pendoContext = useMemo(() => {
        if (!apiKey || !account?.id || !business.name) {
            return null;
        }

        return {
            visitor: buildPendoVisitorMetadata(account),
            account: buildPendoAccountMetadata({
                ...business,
                ongoingCampaignsFeatureEnabled:
                    business.ongoingCampaignsEnabled,
            }),
            signature: buildPendoInitSignature(account, business),
        };
    }, [account, apiKey, business]);

    useEffect(() => {
        let isCancelled = false;

        if (!pendoContext) {
            return undefined;
        }

        const syncPendoMetadata = async () => {
            try {
                await loadPendoScript();
            } catch (error) {
                console.error('Failed to load Pendo script', error);
                return;
            }

            if (isCancelled || !window.pendo) {
                return;
            }

            if (lastPendoInitSignatureRef.current !== pendoContext.signature) {
                hasPendoInitializedRef.current = syncPendoIdentity({
                    visitor: pendoContext.visitor,
                    account: pendoContext.account,
                    hasInitialized: hasPendoInitializedRef.current,
                });
                lastPendoInitSignatureRef.current = pendoContext.signature;
            }
        };

        syncPendoMetadata();

        return () => {
            isCancelled = true;
        };
    }, [pendoContext]);

    return <>{children}</>;
};

export default PendoProvider;
